// Ported from sge_infantil_web/src/utils/months.js + the payment-row merge logic
// embedded in src/pages/payment/pay/index.js#searchFields. The backend endpoint
// (GET api/registration/current/registration/:sucursalId?filterYear=) returns each
// month's propina payment separately from each "extra" (extracurricular fee)
// payment (GET api/extraconfig/student/:sucursalId) — the original app merges the
// two client-side into a single combined status/total per month. Kept as pure
// functions here so ListaPropinasLayer/ConfirmarPagamentoLayer/AmortizacaoLayer can
// share the exact same computation.

export const MONTHS = [
  { code: "1", desc: "Janeiro" },
  { code: "2", desc: "Fevereiro" },
  { code: "3", desc: "Março" },
  { code: "4", desc: "Abril" },
  { code: "5", desc: "Maio" },
  { code: "6", desc: "Junho" },
  { code: "7", desc: "Julho" },
  { code: "8", desc: "Agosto" },
  { code: "9", desc: "Setembro" },
  { code: "10", desc: "Outubro" },
  { code: "11", desc: "Novembro" },
  { code: "12", desc: "Dezembro" },
];

export const monthName = (code) => MONTHS.find((m) => String(m.code) === String(code))?.desc || "-";

export const monthShortName = (code) => monthName(code).slice(0, 3);

export const formatCurrency = (value) => {
  const num = parseFloat(value);
  const safe = Number.isFinite(num) ? num : 0;
  return safe.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const formatCurrencyMZN = (value) => formatCurrency(value);

export const isPaymentDelayed = (date) => {
  if (!date) return false;
  const limit = new Date(date);
  if (Number.isNaN(limit.getTime())) return false;
  return new Date() > limit;
};

const toArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : Object.values(value);
};

// Flattens { extraconfigs: [{name, recurringAmount, id, payments: [...]}] } for one
// student into a single list of monthly extra-payments tagged with the extra's name.
const flattenExtraconfigPayments = (studentExtraconfigs) => {
  const extras = toArray(studentExtraconfigs?.extraconfigs);
  const flattened = [];
  extras.forEach((extra) => {
    toArray(extra?.payments).forEach((payment) => {
      flattened.push({
        ...payment,
        taxName: extra.name,
        recurringAmount: extra.recurringAmount,
        extraId: extra.id,
        extraconfigStudentsId: studentExtraconfigs?.id,
      });
    });
  });
  return flattened;
};

// registrations: raw array from listActiveStudents(filterYear).
// extraconfigsData: raw array from listStudentExtraconfigs(sucursalId), already
// filtered by year by the caller.
// frequencies: raw array from listFrequencies().
export const buildPropinaRows = (registrations, extraconfigsData, frequencies, filterYear) => {
  const activeRegistrations = (registrations || []).filter(
    (reg) => !reg.unregularized && (Number(reg.lastRegistrationYear) === Number(filterYear) || Number(reg.year) === Number(filterYear)),
  );

  return activeRegistrations.map((reg) => {
    const freq = (frequencies || []).find(
      (frq) => String(frq.id) === String(reg?.corseId) || frq.level === reg.student?.level,
    );

    const studentExtraconfigs = (extraconfigsData || []).find(
      (ec) => ec.student?.id === reg.student?.id,
    );
    const extraconfigPayments = flattenExtraconfigPayments(studentExtraconfigs);

    const processedPayments = toArray(reg.payments).map((payment) => {
      const extrasForMonth = extraconfigPayments.filter(
        (extra) => String(extra.month) === String(payment.month),
      );
      const extrasTotal = extrasForMonth.reduce((sum, e) => sum + (parseFloat(e.total) || 0), 0);
      const extrasUnpaid = extrasForMonth.reduce(
        (sum, e) => (Number(e.status) === 0 ? sum + (parseFloat(e.total) || 0) : sum),
        0,
      );
      const extrasStatus =
        extrasForMonth.length > 0 ? (extrasForMonth.every((e) => Number(e.status) === 1) ? 1 : 0) : null;

      const rawStatus = Number(payment.status);
      let combinedStatus = rawStatus;
      if (extrasTotal > 0) {
        if (rawStatus === 1 && extrasStatus === 1) combinedStatus = 1; // propina + extras all paid
        else if (rawStatus === 1 && extrasStatus === 0) combinedStatus = 2; // propina paid, extras pending
        else if (rawStatus === 2) combinedStatus = 2; // propina itself only partially paid
        else combinedStatus = 0; // propina fully unpaid
      }

      // `total` never embeds the fine — it's always kept separately in `fine`/`hasFine`.
      // Trust `fine` directly rather than gating it on `hasFine === 1` — must match
      // getPaymentDebt below exactly, otherwise combinedTotal and the computed debt
      // disagree on whether a fine is owed and "Valor Pago" (combinedTotal - debt)
      // comes out wrong for any record where hasFine/fine end up desynced.
      const fineAmount = parseFloat(payment.fine) || 0;

      return {
        ...payment,
        extrasTotal,
        extrasUnpaid,
        extrasForMonth,
        combinedTotal: (parseFloat(payment.total) || 0) + fineAmount + extrasTotal,
        status: combinedStatus,
        originalStatus: payment.status,
        limit: payment.limitDate || payment.limit,
      };
    });

    return {
      registrationId: reg.id,
      key: reg.student?.id,
      name: reg.student?.name,
      student: reg.student,
      currentMonthlyPayment: reg.student?.currentMonthlyPayment,
      createdAt: reg.student?.createdAt,
      payments: processedPayments,
      studentId: reg.student?.id,
      code: reg.student?.code,
      mobile: reg.student?.mobile,
      level: freq?.description,
      corseId: reg?.corseId || reg?.student?.corseId || reg?.level || reg?.student?.level,
      fine: reg.fine,
      hasEnrolled: reg?.hasEnrolled,
      enrollmentYear: reg?.enrollmentYear,
      overdueAmount: reg.overdueAmount,
      isDiscountNumeric: reg?.isDiscountNumeric,
      registrationMonthlyPayment: reg?.monthlyPayment,
      scheduleType: reg?.scheduleType,
      aggregatedInvoices: reg?.aggregatedInvoices || [],
      taxPayments: toArray(reg.taxPayments),
      year: reg.year || reg.lastRegistrationYear || filterYear,
      // registration.carierContact is only backfilled at enrolment time and never
      // resynced — student.carier.contact (current value) should always be
      // preferred; this is only a fallback for registrations without a carier.
      carierContact: reg.carierContact,
    };
  });
};

// The guardian contact actually in effect for a registration row (as built by
// buildPropinaRows) — mirrors the backend's own fallback in POST /pay-group.
export const effectiveCarierContact = (row) =>
  (row?.student?.carier?.contact || row?.carierContact || "").toString().trim();

// Outstanding amount for a single already-processed payment (as built by
// buildPropinaRows above). The status-based calculation below is the source of
// truth; payment.outstandingAmount from the backend is only used as a fallback
// for the rare case where payment.status itself isn't a usable number (so the
// rules below have nothing to branch on). status/hasFine can both arrive from
// the API as strings, hence the explicit Number() conversions rather than
// strict comparisons. Status 1 (Pago) always means no remainder — a fine can
// only ever be left outstanding on a status 2 (Parcial) month. Status 2 can
// happen for three different reasons and the debt shown must match whichever
// one actually applies:
//  - the propina itself was paid with a partial amount (payment.paidAmount is
//    recorded and is below propina+fine) → debt is what's left of that —
//    e.g. total === paidAmount with a fine still set reduces to exactly the
//    fine, since (total+fine)-paidAmount = fine;
//  - the propina+fine were fully paid but the fine alone was left out
//    (hasFine/fine still set, no paidAmount recorded) → debt is just the fine;
//  - the propina (and fine, if any) is fully settled but this month's extras
//    aren't → debt is only the unpaid extras.
// These aren't mutually exclusive with unpaid extras, so extrasUnpaid is
// always added on top.
export const getPaymentDebt = (payment) => {
  const extrasUnpaid = parseFloat(payment.extrasUnpaid) || 0;
  const status = Number(payment.status);

  if (Number.isNaN(status)) {
    // No usable status to calculate from — fall back to the backend's own figure.
    const backendOutstanding = parseFloat(payment.outstandingAmount);
    if (payment.outstandingAmount != null && Number.isFinite(backendOutstanding)) {
      return backendOutstanding + extrasUnpaid;
    }
    return extrasUnpaid;
  }

  if (status === 1) return 0;

  const total = parseFloat(payment.total) || 0;
  // Trust `fine` directly rather than gating it on `hasFine === 1`. Every place that
  // intentionally clears a fine (routes/payment.js `/pay/:id` when forgiven,
  // `/remove-fine/:id`) always zeroes `fine` and `hasFine` together — there is no
  // legitimate state where hasFine is 0 but fine still holds a real amount. In
  // practice some records (e.g. bulk/legacy writes) desync the two — hasFine ends up
  // 0 while fine keeps a genuine non-zero value — and gating on hasFine silently drops
  // that debt to 0, which is wrong: the money is still owed. `payment.fine` itself is
  // already treated as authoritative everywhere else this data is shown (the "Multa"
  // row in ListaPropinasLayer/ConfirmarPagamentoLayer checks `payment.fine > 0`, not
  // hasFine), so this must match.
  const fine = parseFloat(payment.fine) || 0;

  if (status === 0) {
    return total + fine + extrasUnpaid;
  }

  // status === 2 (Parcial)
  const propinaWithFine = total + fine;
  const paidAmount = payment.paidAmount != null ? parseFloat(payment.paidAmount) || 0 : null;
  if (paidAmount !== null && paidAmount > 0 && paidAmount < propinaWithFine) {
    return propinaWithFine - paidAmount + extrasUnpaid;
  }
  if (fine > 0) {
    return fine + extrasUnpaid;
  }
  return extrasUnpaid;
};

// The propina+fine portion alone of getPaymentDebt above, with the unpaid-extras
// component stripped out — this is what "Pagar Remanescente" (ListaPropinasLayer /
// ConfirmarPagamentoLayer) charges: extras always stay on their own separate
// "Pagar Extras" flow, so a remainder payment must never include them.
export const getPropinaRemaining = (payment) => {
  const extrasUnpaid = parseFloat(payment.extrasUnpaid) || 0;
  return Math.max(0, getPaymentDebt(payment) - extrasUnpaid);
};

// Sums the overdue debt across a student's already-processed payments — only counts
// months whose limit date has passed (future/unbilled months never count).
export const getTotalOverdueDebt = (payments) => {
  if (!Array.isArray(payments)) return 0;
  return payments
    .filter((p) => {
      if (!p.limit) return false;
      const status = +p.status;
      return (status === 0 || status === 2) && isPaymentDelayed(p.limit);
    })
    .reduce((sum, p) => sum + getPaymentDebt(p), 0);
};

// Fallback used only if GET api/paymentTypes/:sucursalId (services/paymentTypes.js)
// fails or returns empty — e.g. offline, or a sucursal not yet seeded by
// sge_infantil_core/scripts/seed-payment-types.js. The real, per-sucursal
// source of truth is now the SGE_PAYMENT_TYPES table, not this array.
export const PAYMENT_METHODS = [
  "Transferência Mpesa",
  "Transferência eMola",
  "Transferência mKesh",
  "Transferência Bancária",
  "Depósito Bancário",
  "POS",
  "Cheque",
  "Numerário",
];
